import { Mic, Music2, Upload, Drum, Menu, Trash2 } from 'lucide-react';
import { ProcessingLimitDisplay } from '@/components/ProcessingLimitDisplay';
import { parseSongInfo } from '@/utils/songUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from "@/components/ui/button";
import { Sidebar, SidebarContent, SidebarHeader, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarMenuAction, useSidebar } from "@/components/ui/sidebar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useState } from "react";
interface SidebarNavigationProps {
  sessions: any[];
  acapellaSessions: any[];
  drumSessions: any[];
  activeSession: string | null;
  onSessionSelect: (sessionId: string | null, type?: 'stem' | 'acapella' | 'drum') => void;
  onSessionDelete?: (sessionId: string, type: 'stem' | 'acapella' | 'drum') => void;
  showStemSplitter?: boolean;
  showAcapella?: boolean;
  showDrumRemover?: boolean;
}
export const SidebarNavigation = ({
  sessions,
  acapellaSessions,
  drumSessions,
  activeSession,
  onSessionSelect,
  onSessionDelete,
  showStemSplitter = true,
  showAcapella = true,
  showDrumRemover = true
}: SidebarNavigationProps) => {
  const isMobile = useIsMobile();
  const {
    open,
    toggleSidebar
  } = useSidebar();
  const [sessionToDelete, setSessionToDelete] = useState<{
    id: string;
    type: 'stem' | 'acapella' | 'drum';
  } | null>(null);
  if (isMobile && !open) {
    return null;
  }
  const handleDeleteConfirm = () => {
    if (sessionToDelete && onSessionDelete) {
      onSessionDelete(sessionToDelete.id, sessionToDelete.type);
      setSessionToDelete(null);
    }
  };
  const handleDeleteCancel = () => {
    setSessionToDelete(null);
  };
  return <>
      <Sidebar className="bg-sidebar border-none">
        <SidebarHeader className="p-4">
          {isMobile && <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-10 w-10" onClick={toggleSidebar}>
                    <Menu className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {open ? "Close menu" : "Open menu"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
          <div className="flex">
            <img src="/lovable-uploads/7c2cb694-9ce5-46b4-8e24-19579fce26cc.png" alt="AI Stem Splitter Logo" className="h-6 object-contain" />
          </div>
        </SidebarHeader>
        <SidebarContent className="flex flex-col h-full">
          <SidebarMenu className="flex-1 space-y-2 px-4">
            <Accordion type="multiple" defaultValue={["stems", "acapella", "drums"]} className="w-full">
              {showStemSplitter && <AccordionItem value="stems" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Music2 className="w-4 h-4" />
                      <span className="text-sm font-normal">AI Stem Splitter</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton onClick={() => onSessionSelect(null, 'stem')} className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}>
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      {sessions?.map(session => <SidebarMenuItem key={session.id} className="group/menu-item">
                          <SidebarMenuButton onClick={() => onSessionSelect(session.id, 'stem')} className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                            <Music2 className="w-4 h-4" />
                            <span>{parseSongInfo(session.original_filename).title}</span>
                          </SidebarMenuButton>
                          {onSessionDelete && <SidebarMenuAction showOnHover onClick={e => {
                      e.stopPropagation();
                      setSessionToDelete({
                        id: session.id,
                        type: 'stem'
                      });
                    }} aria-label="Delete session" className="hover:bg-red-500 hover:text-white my-auto py-[6px] px-[14px]">
                              <Trash2 className="w-3.5 h-3.5" />
                            </SidebarMenuAction>}
                        </SidebarMenuItem>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>}
              {showAcapella && <AccordionItem value="acapella" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Mic className="w-4 h-4" />
                      <span className="text-sm font-normal">Acapella Extractor</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton onClick={() => onSessionSelect(null, 'acapella')} className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}>
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      {acapellaSessions?.map(session => <SidebarMenuItem key={session.id} className="group/menu-item">
                          <SidebarMenuButton onClick={() => onSessionSelect(session.id, 'acapella')} className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                            <Mic className="w-4 h-4" />
                            <span>{parseSongInfo(session.original_filename).title}</span>
                          </SidebarMenuButton>
                          {onSessionDelete && <SidebarMenuAction showOnHover onClick={e => {
                      e.stopPropagation();
                      setSessionToDelete({
                        id: session.id,
                        type: 'acapella'
                      });
                    }} className="hover:bg-red-500 hover:text-white my-auto" aria-label="Delete session">
                              <Trash2 className="w-3.5 h-3.5" />
                            </SidebarMenuAction>}
                        </SidebarMenuItem>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>}
              {showDrumRemover && <AccordionItem value="drums" className="border-none">
                  <AccordionTrigger className="py-2 hover:no-underline">
                    <div className="flex items-center gap-2">
                      <Drum className="w-4 h-4" />
                      <span className="text-sm font-normal">Drum Remover</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2">
                    <div className="flex flex-col space-y-1">
                      <SidebarMenuItem>
                        <SidebarMenuButton onClick={() => onSessionSelect(null, 'drum')} className={`w-full h-10 px-2 bg-background border border-border hover:bg-accent ${activeSession === null ? 'bg-accent text-accent-foreground' : ''}`}>
                          <Upload className="w-4 h-4" />
                          <span>Upload audio</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      {drumSessions?.map(session => <SidebarMenuItem key={session.id} className="group/menu-item">
                          <SidebarMenuButton onClick={() => onSessionSelect(session.id, 'drum')} className={`w-full h-10 px-2 ${session.id === activeSession ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}`}>
                            <Drum className="w-4 h-4" />
                            <span>{parseSongInfo(session.original_filename).title}</span>
                          </SidebarMenuButton>
                          {onSessionDelete && <SidebarMenuAction showOnHover onClick={e => {
                      e.stopPropagation();
                      setSessionToDelete({
                        id: session.id,
                        type: 'drum'
                      });
                    }} className="hover:bg-red-500 hover:text-white my-auto" aria-label="Delete session">
                              <Trash2 className="w-3.5 h-3.5" />
                            </SidebarMenuAction>}
                        </SidebarMenuItem>)}
                    </div>
                  </AccordionContent>
                </AccordionItem>}
            </Accordion>
          </SidebarMenu>
          <ProcessingLimitDisplay />
        </SidebarContent>
      </Sidebar>

      <AlertDialog open={!!sessionToDelete} onOpenChange={() => !sessionToDelete || setSessionToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this audio file and all extracted stems.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDeleteCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-500 hover:bg-red-600 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>;
};